import React from "react";
import { useLocation } from "react-router-dom";
import { Button, Chip, Stack, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  DebouncedSearch,
  ServerPaginatedTable,
  useDropdownApis,
  useFormatAmount,
} from "../../../shared";
import { arraySort } from "../../../utils";
import {
  FtiBilingualText,
  FtiFormDialog,
  FtiOrderSelectionDialog,
  FullTaxInvoiceListFilterSection,
} from "../components";
import { FTI_STATUS_STYLES } from "../constants";

// State and handlers come from `useFullTaxInvoice` in FullTaxInvoicePage
export const FullTaxInvoiceList = ({
  state,
  isCreateVisible,
  appliedFilters,
  gotoPage,
  setPageSize,
  handleSearch,
  handleFilterChange,
  handleDateChangeApply,
  handleOpenDateRangeSelection,
  handleApplyFilter,
  handleClearFilter,
  handleClearAllFilter,
  handleOpenOrderSelection,
  handleSelectOrder,
  handleCloseCreateFlow,
  handleCreateFti,
  handleFtiCreated,
}) => {
  const formatAmount = useFormatAmount();

  // Store admins only see their own store's invoices, so the store column and filter are super admin only
  const { pathname } = useLocation();
  const isStoreAdmin = pathname.includes("store-admin");
  const { storeList } = useDropdownApis({ isStoreList: !isStoreAdmin });
  const storeOptions = React.useMemo(
    () =>
      arraySort(
        storeList.map(({ id, name }) => ({ value: id, label: name })),
        "label"
      ),
    [storeList]
  );

  const columns = React.useMemo(
    () => [
      {
        Header: <FtiBilingualText en="Invoice No." th="เลขที่ใบกำกับภาษี" />,
        accessor: "fti_invoice_number",
        disableSortBy: true,
      },
      {
        Header: <FtiBilingualText en="Invoice Date" th="วันที่ออกใบกำกับภาษี" />,
        accessor: "issue_date_tz",
        disableSortBy: true,
        Cell: ({ value, row }) => (
          <FtiBilingualText en={value} th={row.original.display_issue_date} />
        ),
      },
      ...(isStoreAdmin
        ? []
        : [
            {
              Header: <FtiBilingualText en="Store Name" th="ชื่อร้านค้า" />,
              accessor: "store_name",
              disableSortBy: true,
              Cell: ({ value }) => value ?? "--",
            },
          ]),
      {
        Header: <FtiBilingualText en="Business Name" th="ชื่อธุรกิจ" />,
        accessor: "business_name",
        disableSortBy: true,
        Cell: ({ value, row }) => (
          <Stack gap="4px" maxWidth="300px">
            <Typography variant="body2">{value}</Typography>
            {/* Long remarks stretched the row, so only one line shows and the full text is on hover */}
            {row.original.remarks && (
              <Tooltip title={row.original.remarks} placement="bottom-start">
                <Typography variant="caption" color="GrayText" noWrap>
                  {row.original.remarks}
                </Typography>
              </Tooltip>
            )}
          </Stack>
        ),
      },
      {
        Header: <FtiBilingualText en="ABB Invoice No." th="เลขที่ใบกำกับภาษี ABB" />,
        accessor: "order_number",
        disableSortBy: true,
      },
      {
        Header: <FtiBilingualText en="Grand Total" th="ยอดรวมสุทธิ" />,
        accessor: "total_amount",
        disableSortBy: true,
        Cell: ({ value }) => formatAmount(value),
      },
      {
        Header: <FtiBilingualText en="Status" th="สถานะ" />,
        accessor: "status",
        disableSortBy: true,
        Cell: ({ value }) =>
          value ? (
            <Chip
              size="small"
              label={value}
              sx={{
                textTransform: "capitalize",
                ...FTI_STATUS_STYLES[value],
              }}
            />
          ) : (
            "--"
          ),
      },
      {
        Header: "Action",
        accessor: "action",
        disableSortBy: true,
        align: "right",
        Cell: () => <img alt="details" src="/icons/arrow_forward.svg" />,
      },
    ],
    [formatAmount, isStoreAdmin]
  );

  return (
    <>
      <ServerPaginatedTable
        columns={columns}
        data={state.invoices}
        pagination={state.pagination}
        isLoading={state.isLoading}
        gotoPage={gotoPage}
        setPageSize={setPageSize}
        handleSort={() => {}}
        sortByGlobal={[]}
        defaultSearch={false}
        filterContent={
          <>
            <DebouncedSearch
              placeholder="Search..."
              value={state.search}
              onChange={handleSearch}
            />
            {isCreateVisible && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                sx={{ minWidth: "fit-content", height: "48px" }}
                onClick={handleOpenOrderSelection}
              >
                Create/สร้าง
              </Button>
            )}
          </>
        }
        filterSection={
          // Filters wrap among themselves so Apply always stays on the right
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            gap="16px"
            width="100%"
          >
            <FullTaxInvoiceListFilterSection
              filter={state.filter}
              storeOptions={isStoreAdmin ? null : storeOptions}
              onFilterChange={handleFilterChange}
              onOpenDateRangeSelection={handleOpenDateRangeSelection}
              onDateChangeApply={handleDateChangeApply}
            />
            <Button
              size="medium"
              variant="contained"
              onClick={handleApplyFilter}
              disabled={state.filter.isFilterApplied}
              sx={{ flexShrink: 0 }}
            >
              Apply
            </Button>
          </Stack>
        }
        filterApplied={appliedFilters}
        onFilterClear={handleClearFilter}
        onAllFilterClear={handleClearAllFilter}
        onRowLink={(row) => `${row.id}/details`}
      />
      <FtiOrderSelectionDialog
        open={state.createFlow.step === "orders"}
        requestPeriodDays={state.settings.requestPeriodDays}
        onClose={handleCloseCreateFlow}
        onSelectOrder={handleSelectOrder}
      />
      <FtiFormDialog
        open={state.createFlow.step === "form"}
        title={`Full tax invoice/ใบกำกับภาษีเต็มรูปสำหรับ ABB เลขที่ ${
          state.createFlow.order?.order_number ?? ""
        }`}
        customer={
          state.createFlow.order && {
            id: state.createFlow.order.customer_id,
            name: state.createFlow.order.customer_name,
            email: state.createFlow.order.email,
          }
        }
        submitApi={handleCreateFti}
        onSuccess={handleFtiCreated}
        onClose={handleCloseCreateFlow}
      />
    </>
  );
};
